import { useCallback, useMemo, useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  ColDef,
  GridReadyEvent,
  GridApi,
  ValueFormatterParams,
  ICellRendererParams,
  IDatasource,
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import * as XLSX from 'xlsx';
import CommonButton from '../../../components/common-components/button/CommonButton';
import CommonDropdown, {
  Option,
} from '../../../components/common-components/dropdown/CommonDropdown';
import styles from './OvertimeManagement.module.css';
import { localeText } from '../../../constants/AgGridLocale';
import { Pencil, Trash2, Plus, RotateCw, Info } from 'lucide-react';
import { useStore } from '../../../hooks/useStore';
import { observer } from 'mobx-react-lite';
import { IOvertimeResult } from '../../../models/overtime/OvertimeModel';
import { IUserManagementResult } from '../../../models/user-management/UserManagementModel';
import DeleteConfirmationDialog from './parts/DeleteConfirmationDialog';
import EditOvertimePopup from './parts/EditOvertimePopup';
import NewOvertimePopup from './parts/NewOvertimePopup';
import NewLeavePopup from './parts/NewLeavePopup';
import RejectionReasonPopup from './parts/RejectionReasonPopup';
import { userManagementService } from '../../../services/modules/userManagement/userManagement.service';

const StatusCellRenderer = (props: ICellRendererParams<IOvertimeResult>) => {
  const getStatusClass = (status: string) => {
    switch (status) {
      case 'Pending':
        return styles.statusPending;
      case 'Approved':
        return styles.statusApproved;
      case 'Rejected':
        return styles.statusRejected;
      default:
        return styles.statusPending;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'Pending':
        return 'Beklemede';
      case 'Approved':
        return 'Onaylandı';
      case 'Rejected':
        return 'Reddedildi';
      default:
        return 'Beklemede';
    }
  };

  return (
    <span className={`${styles.statusBadge} ${getStatusClass(props.value)}`}>
      {getStatusText(props.value)}
    </span>
  );
};

const ActionCellRenderer = (props: ICellRendererParams<IOvertimeResult>) => {
  const { popupStore, overtimeStore } = useStore();

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (props.data) {
      overtimeStore.setSelectedOvertime(props.data);
      popupStore.setIsEditOvertimeOpen(true);
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (props.data?.id) {
      overtimeStore.setSelectedOvertimeToDelete(props.data.id);
      popupStore.setIsDeleteOvertimeConfirmationOpen(true);
    }
  };

  const handleShowRejectionReason = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (props.data) {
      overtimeStore.setSelectedOvertime(props.data);
      popupStore.setIsRejectionReasonOpen(true);
    }
  };

  return (
    <div className={styles.actionCell}>
      <button onClick={handleEdit} className={styles.editButton} title="Düzenle">
        <Pencil size={20} />
      </button>
      <button onClick={handleDelete} className={styles.deleteButton} title="Sil">
        <Trash2 size={20} />
      </button>
      {props.data?.status === 'Rejected' && props.data?.rejectionReason && (
        <button
          onClick={handleShowRejectionReason}
          className={styles.infoButton}
          title="Red Sebebini Görüntüle"
        >
          <Info size={20} />
        </button>
      )}
    </div>
  );
};

const OvertimeManagement = () => {
  const [gridApi, setGridApi] = useState<GridApi<IOvertimeResult> | null>(null);
  const [viewportWidth, setViewportWidth] = useState<number>(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );
  const [users, setUsers] = useState<IUserManagementResult[]>([]);
  const { popupStore, overtimeStore, authStore } = useStore();

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Admin ise kullanıcı listesini yükle
  useEffect(() => {
    const loadUsers = async () => {
      if (authStore.isAdmin) {
        try {
          const usersList = await userManagementService.getAllUsers();
          // OData yanıtı { value: [...] } formatında geliyor
          const actualUsers = Array.isArray(usersList)
            ? usersList
            : (usersList as any)?.value || [];
          setUsers(actualUsers);
        } catch (error) {
          console.error('Error loading users:', error);
        }
      }
    };

    loadUsers();
  }, [authStore.isAdmin]);

  const dataSource = useMemo((): IDatasource => {
    return {
      getRows: async params => {
        try {
          const result = await overtimeStore.listOvertimes({
            startRow: params.startRow,
            endRow: params.endRow,
            filterModel: params.filterModel,
            sortModel: params.sortModel,
          });
          if (result) {
            params.successCallback(result.rows, result.lastRow);
          } else {
            params.failCallback();
          }
        } catch (error) {
          console.error('Data loading error:', error);
          params.failCallback();
        }
      },
    };
  }, [overtimeStore]);

  const onGridReady = useCallback(
    (params: GridReadyEvent<IOvertimeResult>) => {
      setGridApi(params.api);
      overtimeStore.setGridApi(params.api);
    },
    [overtimeStore]
  );

  const exportToExcel = useCallback(async () => {
    if (!gridApi) return;

    gridApi.showLoadingOverlay();

    try {
      const result = await overtimeStore.listOvertimes({
        startRow: 0,
        endRow: 1000000,
        filterModel: gridApi.getFilterModel(),
        sortModel: null,
      });

      if (!result?.rows.length) {
        gridApi.hideOverlay();
        return;
      }

      const exportData = result.rows.map(item => ({
        ID: item.id,
        Kullanıcı: item.userName,
        'Başlangıç Tarihi': new Date(item.startDate).toLocaleString('tr-TR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }),
        'Bitiş Tarihi': new Date(item.endDate).toLocaleString('tr-TR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }),
        'Toplam Saat': item.totalHours,
        Açıklama: item.description,
        Tür: item.overtimeType === 'Overtime' ? 'Mesai' : 'İzin',
        Durum:
          item.status === 'Pending'
            ? 'Beklemede'
            : item.status === 'Approved'
            ? 'Onaylandı'
            : 'Reddedildi',
        'Oluşturulma Tarihi': new Date(item.createdDate).toLocaleDateString('tr-TR'),
        Onaylayan: item.approvedBy || '',
        'Onay Tarihi': item.approvedDate
          ? new Date(item.approvedDate).toLocaleDateString('tr-TR')
          : '',
        'Red Sebebi': item.rejectionReason || '',
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Mesai Kayitlari');
      XLSX.writeFile(wb, `Mesai_Kayitlari_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (error) {
      console.error('Excel export error:', error);
    } finally {
      gridApi.hideOverlay();
    }
  }, [gridApi, overtimeStore]);

  const columnDefs = useMemo<ColDef<IOvertimeResult>[]>(
    () => [
      {
        headerName: 'İşlemler',
        width: 160,
        pinned: 'left',
        sortable: false,
        filter: false,
        cellRenderer: ActionCellRenderer,
        cellStyle: () => ({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 8px',
          backgroundColor: 'transparent',
        }),
      },
      {
        field: 'id',
        headerName: 'ID',
        width: 80,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          suppressAndOrCondition: true,
          maxNumConditions: 1,
        },
      },
      {
        field: 'userName',
        headerName: 'Kullanıcı',
        minWidth: 150,
        flex: 1,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          suppressAndOrCondition: true,
          maxNumConditions: 1,
        },
      },
      {
        field: 'startDate',
        headerName: 'Başlangıç Tarihi',
        minWidth: 150,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          suppressAndOrCondition: true,
          maxNumConditions: 1,
        },
        valueFormatter: (params: ValueFormatterParams) => {
          if (!params.value) return '';
          return new Date(params.value).toLocaleString('tr-TR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          });
        },
      },
      {
        field: 'endDate',
        headerName: 'Bitiş Tarihi',
        minWidth: 150,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          suppressAndOrCondition: true,
          maxNumConditions: 1,
        },
        valueFormatter: (params: ValueFormatterParams) => {
          if (!params.value) return '';
          return new Date(params.value).toLocaleString('tr-TR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          });
        },
      },
      {
        field: 'totalHours',
        headerName: 'Toplam Saat',
        width: 120,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          suppressAndOrCondition: true,
          maxNumConditions: 1,
        },
      },
      {
        field: 'description',
        headerName: 'Açıklama',
        minWidth: 200,
        flex: 2,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          suppressAndOrCondition: true,
          maxNumConditions: 1,
        },
      },
      {
        field: 'overtimeType',
        headerName: 'Tür',
        width: 100,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          suppressAndOrCondition: true,
          maxNumConditions: 1,
        },
        valueFormatter: (params: ValueFormatterParams) => {
          return params.value === 'Overtime' ? 'Mesai' : 'İzin';
        },
      },
      {
        field: 'status',
        headerName: 'Durum',
        width: 120,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          suppressAndOrCondition: true,
          maxNumConditions: 1,
        },
        cellRenderer: StatusCellRenderer,
        hide: !authStore.hasAnyRole(['Admin']), // Sadece admin görür
      },
      {
        field: 'createdDate',
        headerName: 'Oluşturulma Tarihi',
        minWidth: 160,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          suppressAndOrCondition: true,
          maxNumConditions: 1,
        },
        valueFormatter: (params: ValueFormatterParams) => {
          if (!params.value) return '';
          return new Date(params.value).toLocaleDateString('tr-TR');
        },
      },
      {
        field: 'approvedBy',
        headerName: 'Onaylayan/Reddeden',
        minWidth: 150,
        flex: 1,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          suppressAndOrCondition: true,
          maxNumConditions: 1,
        },
      },
    ],
    []
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      suppressMovable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains'],
        defaultOption: 'contains',
        buttons: ['apply', 'reset'],
        closeOnApply: true,
        suppressAndOrCondition: true,
        maxNumConditions: 1,
      },
    }),
    []
  );

  const isMobile = viewportWidth < 768;
  const isTablet = viewportWidth >= 768 && viewportWidth < 1024;
  const gridRowHeight = isMobile ? 40 : isTablet ? 44 : 48;
  const gridHeaderHeight = isMobile ? 40 : isTablet ? 44 : 48;
  const pageSize = isMobile ? 10 : 20;

  const handleNewOvertime = () => {
    popupStore.setIsNewOvertimeOpen(true);
  };

  const handleNewLeave = () => {
    popupStore.setIsNewLeaveOpen(true);
  };

  const handleRefresh = () => {
    overtimeStore.refreshGrid();
  };

  const handleUserChange = (userId: string | null) => {
    // "all" seçildiğinde null olarak ayarla
    overtimeStore.setSelectedUserId(userId === 'all' ? null : userId);
    overtimeStore.refreshGrid();
  };

  const getRowStyle = (params: any) => {
    if (!params.data) return undefined;

    const status = params.data.status;

    if (status === 'Approved') {
      return { backgroundColor: '#d4edda' };
    }
    if (status === 'Pending') {
      return { backgroundColor: '#fff3cd' };
    }
    if (status === 'Rejected') {
      return { backgroundColor: '#f8d7da' };
    }

    return undefined;
  };

  const userOptions: Option<string>[] = useMemo(() => {
    const baseOptions: Option<string>[] = [{ value: 'all', label: 'Tüm Kullanıcılar' }];

    // users array olduğundan emin olalım
    if (Array.isArray(users) && users.length > 0) {
      const mappedUserOptions: Option<string>[] = users.map(user => ({
        value: user.id,
        label: `${user.userName}${
          user.firstName && user.lastName ? ` (${user.firstName} ${user.lastName})` : ''
        }`,
      }));
      return [...baseOptions, ...mappedUserOptions];
    }

    return baseOptions;
  }, [users]);

  return (
    <>
      <div className={styles.container}>
        <div className={styles.header}>
          <h2 className={styles.headerTitle}>Mesai Yönetimi</h2>
          {authStore.isAdmin && (
            <div className={styles.userSelector}>
              <CommonDropdown
                options={userOptions}
                value={overtimeStore.selectedUserId ? overtimeStore.selectedUserId : 'all'}
                onChange={handleUserChange}
                label="Kullanıcı Seçin"
                className={styles.dropdown}
              />
            </div>
          )}
          <div className={styles.buttonContainer}>
            <CommonButton type="primary" onClick={handleNewOvertime} leftIcon={<Plus size={18} />}>
              Yeni Mesai Kaydı
            </CommonButton>
            <CommonButton type="primary" onClick={handleNewLeave} leftIcon={<Plus size={18} />}>
              İzin Ekle
            </CommonButton>
            <CommonButton type="primary" onClick={exportToExcel}>
              Excel'e Aktar
            </CommonButton>
            <CommonButton
              type="secondary"
              onClick={handleRefresh}
              leftIcon={<RotateCw size={18} />}
            >
              Yenile
            </CommonButton>
          </div>
        </div>

        <div className={styles.gridContainer}>
          <div className="ag-theme-alpine" style={{ height: '100%', width: '100%' }}>
            <AgGridReact
              rowModelType={'infinite'}
              cacheBlockSize={20}
              maxBlocksInCache={1}
              datasource={dataSource}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              onGridReady={onGridReady}
              getRowStyle={getRowStyle}
              pagination={true}
              paginationPageSize={pageSize}
              rowSelection="multiple"
              rowHeight={gridRowHeight}
              headerHeight={gridHeaderHeight}
              animateRows={true}
              suppressColumnVirtualisation={true}
              suppressRowVirtualisation={true}
              localeText={localeText}
            />
          </div>
        </div>
      </div>

      {popupStore.isNewOvertimeOpen && <NewOvertimePopup />}
      {popupStore.isNewLeaveOpen && <NewLeavePopup />}
      {popupStore.isEditOvertimeOpen && <EditOvertimePopup />}
      {popupStore.isRejectionReasonOpen && <RejectionReasonPopup />}
      <DeleteConfirmationDialog
        isOpen={popupStore.isDeleteOvertimeConfirmationOpen}
        onClose={() => popupStore.setIsDeleteOvertimeConfirmationOpen(false)}
        onConfirm={async () => {
          if (overtimeStore.selectedOvertimeToDelete) {
            await overtimeStore.deleteOvertime(overtimeStore.selectedOvertimeToDelete);
            overtimeStore.setSelectedOvertimeToDelete(null);
          }
          popupStore.setIsDeleteOvertimeConfirmationOpen(false);
        }}
        title="Mesai Kaydı Silme Onayı"
        message="Bu mesai kaydını silmek istediğinizden emin misiniz?"
      />
    </>
  );
};

export default observer(OvertimeManagement);
